
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HelmetProvider } from "react-helmet-async";
import App from "@/App";
import { AuthProvider } from "@/context/AuthContext";
import { ToastProvider } from "@/context/ToastContext";
import { Theme } from "@radix-ui/themes";
import "@radix-ui/themes/styles.css";
import "@/styles/global.css";
import "@/styles/index.css";
import { ErrorHandlerProvider } from "./context/ErrorHandlerContext";
import { CustomRollbarProvider } from "./providers/CustomRollbarProvider";
import "./utils/amplitude";

// Suppress Vite's default re-throw on chunk load failures so the error never
// reaches the ErrorBoundary. lazyWithRetry() owns the reload logic.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on rate limit errors (429) or auth errors (401, 403)
        if (error?.response?.status === 429) return false;
        if (error?.response?.status === 401) return false;
        if (error?.response?.status === 403) return false;
        // Retry other errors up to 1 time
        return failureCount < 1;
      },
    },
  },
});


createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <CustomRollbarProvider>
      {/* <StrictMode> */}
      <Theme appearance="light" accentColor="blue" scaling="95%">
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <ErrorHandlerProvider>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </ErrorHandlerProvider>
            </ToastProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </BrowserRouter>
      </Theme>
    </CustomRollbarProvider>
  </HelmetProvider>,
);
